import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Instagram } from "lucide-react";
import { cn, getBackendUrl, getImageUrl } from "../../lib/utils";
import axios from "axios";

const languageFlags = {
  tr: { flag: 'tr', label: 'Türkçe', code: 'tr' },
  en: { flag: 'us', label: 'English', code: 'en' },
  de: { flag: 'de', label: 'Deutsch', code: 'de' },
  ru: { flag: 'ru', label: 'Русский', code: 'ru' },
  fr: { flag: 'fr', label: 'Français', code: 'fr' },
  ar: { flag: 'sa', label: 'العربية', code: 'ar' },
  es: { flag: 'es', label: 'Español', code: 'es' }
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [activeLanguages, setActiveLanguages] = useState(['tr', 'en']);
  const [currentLang, setCurrentLang] = useState('tr');
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch logo and active languages
  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = getBackendUrl();
        const { data } = await axios.get(`${url}/api/settings`);
        if (data) {
          if (data.footer_settings && data.footer_settings.logo_url) {
            setLogoUrl(data.footer_settings.logo_url);
          }
          if (data.language_settings && Array.isArray(data.language_settings.active_languages)) {
            setActiveLanguages(data.language_settings.active_languages);
          }
        }
      } catch (e) {
        console.error("Error loading Navbar configuration", e);
      }
    };
    fetchData();
  }, []);

  // Detect current language from googtrans cookie
  useEffect(() => {
    const getCookie = (name) => {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for(let i=0;i < ca.length;i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1,c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length,c.length);
      }
      return null;
    };
    
    const cookie = getCookie('googtrans');
    if (cookie) {
      const parts = cookie.split('/');
      const code = parts[parts.length - 1];
      if (code && languageFlags[code]) {
        setCurrentLang(code);
      } else {
        setCurrentLang('tr');
      }
    } else {
      setCurrentLang('tr');
    }
  }, []);

  // Initialize Google Translate Web Element script and hide components
  useEffect(() => {
    // 1. Inject hidden target element
    if (!document.getElementById('google_translate_element')) {
      const div = document.createElement('div');
      div.id = 'google_translate_element';
      div.style.display = 'none';
      document.body.appendChild(div);
    }

    // 2. Inject translation script
    if (!document.getElementById('google-translate-script')) {
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);

      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement({
          pageLanguage: 'tr',
          autoDisplay: false
        }, 'google_translate_element');
      };
    }

    // 3. Inject styling to hide google translate banners
    const styleId = 'google-translate-hiding-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        #google_translate_element, .skiptranslate, .goog-te-banner-frame, .goog-te-balloon-frame {
          display: none !important;
        }
        body {
          top: 0px !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleLanguageChange = (code) => {
    const setCookie = (name, value, days) => {
      let expires = "";
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = "; expires=" + date.toUTCString();
      }
      document.cookie = name + "=" + (value || "") + expires + "; path=/";
      
      const host = window.location.hostname;
      if (host !== 'localhost' && host !== '127.0.0.1') {
        document.cookie = name + "=" + (value || "") + expires + "; path=/; domain=." + host;
        const parts = host.split('.');
        if (parts.length > 2) {
          const domain = parts.slice(-2).join('.');
          document.cookie = name + "=" + (value || "") + expires + "; path=/; domain=." + domain;
        }
      }
    };

    const deleteCookie = (name) => {
      document.cookie = name + '=; Max-Age=-99999999; path=/';
      const host = window.location.hostname;
      if (host !== 'localhost' && host !== '127.0.0.1') {
        document.cookie = name + '=; Max-Age=-99999999; path=/; domain=.' + host;
        const parts = host.split('.');
        if (parts.length > 2) {
          const domain = parts.slice(-2).join('.');
          document.cookie = name + '=; Max-Age=-99999999; path=/; domain=.' + domain;
        }
      }
    };

    if (code === 'tr') {
      deleteCookie('googtrans');
    } else {
      setCookie('googtrans', `/tr/${code}`, 30);
    }
    
    // Reload page to apply translation
    window.location.reload();
  };

  const navLinks = [
    { name: "Ana Sayfa", path: "/" },
    { 
      name: "Kurumsal", 
      path: "#",
      dropdown: [
        { name: "Hakkımızda", path: "/kurumsal/hakkimizda" },
        { name: "Yönetim Kurulu", path: "/kurumsal/yonetim-kurulu" },
        { name: "Misyon & Vizyon", path: "/kurumsal/misyon-vizyon" },
        { name: "Kalite Politikamız", path: "/kurumsal/kalite-politikasi" },
        { name: "İnsan Kaynakları", path: "/kurumsal/insan-kaynaklari" },
      ]
    },
    { name: "Haberler", path: "/haberler" },
    { name: "Galeri", path: "/galeri" },
    { name: "İletişim", path: "/iletisim" },
  ];

  const isHome = location.pathname === "/";
  const needsDarkText = isScrolled || !isHome || mobileMenuOpen;

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        needsDarkText
          ? "bg-white/95 backdrop-blur-lg border-b border-gray-200 py-3 shadow-sm"
          : "bg-transparent py-5"
      )}
    >
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={logoUrl ? getImageUrl(logoUrl) : "https://isilkoop.com/assets/images/logo/logo-dark.png"} 
              alt="İSİLKOOP Logo" 
              className={cn(
                "h-10 md:h-12 object-contain transition-all duration-500",
                !needsDarkText ? "brightness-0 invert" : ""
              )}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link, idx) => (
              <div 
                key={idx} 
                className="relative group"
                onMouseEnter={() => setActiveDropdown(idx)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.dropdown ? (
                  <div className={cn(
                    "flex items-center gap-1 cursor-pointer text-sm font-semibold transition-colors hover:text-blue-500 py-2",
                    needsDarkText ? "text-slate-700" : "text-white"
                  )}>
                    {link.name}
                    <ChevronDown size={16} className={cn("transition-transform duration-200", activeDropdown === idx ? "rotate-180" : "")} />
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className={cn(
                      "text-sm font-semibold transition-colors hover:text-blue-500 relative py-2 block",
                      needsDarkText ? "text-slate-700" : "text-white"
                    )}
                  >
                    {link.name}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-500 transition-all group-hover:w-full"></span>
                  </Link>
                )}

                {/* Desktop Dropdown */}
                {link.dropdown && (
                  <div className={cn(
                    "absolute top-full left-0 w-60 bg-white rounded-xl shadow-xl border border-slate-100 py-3 transition-all duration-300 transform origin-top-left",
                    activeDropdown === idx ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
                  )}>
                    {link.dropdown.map((dropItem, dIdx) => (
                      <Link
                        key={dIdx}
                        to={dropItem.path}
                        className="block px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      >
                        {dropItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            <div className="flex items-center gap-5 ml-4 pl-4 border-l border-slate-200/40">
              
              {/* Language Selector Dropdown */}
              <div className="relative flex items-center">
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200/40 hover:bg-slate-100/10 text-sm font-semibold transition-all",
                    needsDarkText ? "text-slate-700 hover:bg-slate-100" : "text-white"
                  )}
                >
                  {languageFlags[currentLang] && (
                    <img 
                      src={`https://flagcdn.com/w40/${languageFlags[currentLang].flag}.png`} 
                      alt={languageFlags[currentLang].label} 
                      className="w-4 h-3 object-cover rounded-sm shadow-sm" 
                    />
                  )}
                  <span className="uppercase text-xs leading-none">{currentLang}</span>
                  <ChevronDown size={14} className={cn("transition-transform duration-200", langDropdownOpen ? "rotate-180" : "")} />
                </button>

                {langDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setLangDropdownOpen(false)}></div>
                    <div className="absolute right-0 mt-2 top-full w-36 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-50 transform origin-top-right transition-all animate-fade-in">
                      {activeLanguages.map((code) => {
                        const lang = languageFlags[code];
                        if (!lang) return null;
                        return (
                          <button
                            key={code}
                            onClick={() => {
                              handleLanguageChange(code);
                              setLangDropdownOpen(false);
                            }}
                            className={cn(
                              "flex items-center gap-2.5 w-full px-4 py-2.5 text-xs font-bold text-left hover:bg-blue-50 hover:text-blue-600 transition-colors",
                              currentLang === code ? "text-blue-600 bg-blue-50/40" : "text-slate-700"
                            )}
                          >
                            <img 
                              src={`https://flagcdn.com/w40/${lang.flag}.png`} 
                              alt={lang.label} 
                              className="w-4.5 h-3.5 object-cover rounded-sm shadow-sm" 
                            />
                            <span>{lang.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <a 
                href="https://instagram.com/isilkoop" 
                target="_blank" 
                rel="noreferrer"
                className={cn(
                  "transition-colors hover:text-pink-500",
                  needsDarkText ? "text-slate-700" : "text-white"
                )}
              >
                <Instagram size={20} />
              </a>

              <Link
                to="/iletisim"
                className={cn(
                  "px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 shadow-md",
                  needsDarkText
                    ? "bg-primary text-white hover:bg-blue-600 hover:shadow-lg"
                    : "bg-white text-primary hover:bg-gray-100 hover:scale-105"
                )}
              >
                Bize Ulaşın
              </Link>
            </div>
          </nav>

          {/* Mobile Menu Actions */}
          <div className="flex items-center gap-3 lg:hidden">
            {/* Language Selector Dropdown (Mobile Header) */}
            <div className="relative flex items-center">
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-slate-200/40 hover:bg-slate-100/10 text-xs font-bold transition-all",
                  needsDarkText ? "text-slate-700 hover:bg-slate-100" : "text-white"
                )}
              >
                {languageFlags[currentLang] && (
                  <img 
                    src={`https://flagcdn.com/w40/${languageFlags[currentLang].flag}.png`} 
                    alt={languageFlags[currentLang].label} 
                    className="w-4 h-3 object-cover rounded-sm shadow-sm" 
                  />
                )}
                <span className="uppercase text-xs leading-none">{currentLang}</span>
                <ChevronDown size={14} className={cn("transition-transform duration-200", langDropdownOpen ? "rotate-180" : "")} />
              </button>

              {langDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLangDropdownOpen(false)}></div>
                  <div className="absolute right-0 mt-2 top-full w-32 bg-white rounded-2xl shadow-xl border border-slate-100 py-1.5 z-50 transform origin-top-right transition-all animate-fade-in">
                    {activeLanguages.map((code) => {
                      const lang = languageFlags[code];
                      if (!lang) return null;
                      return (
                        <button
                          key={code}
                          onClick={() => {
                            handleLanguageChange(code);
                            setLangDropdownOpen(false);
                          }}
                          className={cn(
                            "flex items-center gap-2.5 w-full px-3.5 py-2 text-xs font-bold text-left hover:bg-blue-50 hover:text-blue-600 transition-colors",
                            currentLang === code ? "text-blue-600 bg-blue-50/40" : "text-slate-700"
                          )}
                        >
                          <img 
                            src={`https://flagcdn.com/w40/${lang.flag}.png`} 
                            alt={lang.label} 
                            className="w-4 h-3 object-cover rounded-sm shadow-sm" 
                          />
                          <span>{lang.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle Button */}
            <button
              className={cn(
                "p-2 transition-colors",
                needsDarkText ? "text-primary" : "text-white"
              )}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <div
        className={cn(
          "lg:hidden absolute top-full left-0 right-0 bg-white border-b shadow-2xl overflow-hidden transition-all duration-300 ease-in-out",
          mobileMenuOpen ? "max-h-screen opacity-100 border-t" : "max-h-0 opacity-0"
        )}
      >
        <div className="flex flex-col p-4 space-y-1">
          {navLinks.map((link, idx) => (
            <div key={idx}>
              {link.dropdown ? (
                <div className="flex flex-col">
                  <button 
                    onClick={() => setActiveDropdown(activeDropdown === idx ? null : idx)}
                    className="flex items-center justify-between w-full py-3 px-4 text-slate-800 font-bold hover:bg-slate-50 rounded-lg text-left"
                  >
                    {link.name}
                    <ChevronDown size={18} className={cn("transition-transform", activeDropdown === idx ? "rotate-180" : "")} />
                  </button>
                  <div className={cn(
                    "overflow-hidden transition-all duration-300",
                    activeDropdown === idx ? "max-h-[400px]" : "max-h-0"
                  )}>
                    <div className="bg-slate-50 rounded-lg mx-4 mb-2 mt-1 p-2 space-y-1">
                      {link.dropdown.map((dropItem, dIdx) => (
                        <Link
                          key={dIdx}
                          to={dropItem.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block py-2.5 px-4 text-sm text-slate-600 hover:text-blue-600 font-semibold rounded-md hover:bg-white transition-colors"
                        >
                          {dropItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 px-4 text-slate-800 font-bold hover:bg-slate-50 rounded-lg"
                >
                  {link.name}
                </Link>
              )}
            </div>
          ))}



          <div className="p-4 mt-2 flex flex-col gap-4">
            <a 
              href="https://instagram.com/isilkoop" 
              target="_blank" 
              rel="noreferrer"
              className="flex items-center justify-center gap-2 py-3 px-4 bg-pink-50 text-pink-600 font-bold rounded-lg"
            >
              <Instagram size={20} /> Instagram'da Takip Et
            </a>
            <Link
              to="/iletisim"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full py-3 px-4 bg-primary text-white text-center font-bold rounded-lg shadow-md"
            >
              Bize Ulaşın
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
